import type { Scope } from '@api/types'
import type { AmlConfig, ScopeData, Recipe, RiskConfig, Workflow } from '../types'
import { COMPLEXITY_OPTIONS } from '../types'
import {
  getDefaultAmlConfig,
  getDefaultBlueprintConfig,
  getDefaultDataInputs,
  getDefaultGeneralInfo,
  getDefaultKybConfig,
  getDefaultKycConfig,
  getDefaultBiometricsConfig,
  getDefaultRulesConfig,
  getDefaultRiskConfig,
  getDefaultScopeData,
  getDefaultSowConfig,
} from './defaults'
import { calculateTabCompletion } from './completion'
import { calculateAutoHours, calculateTotalHours } from '../hourCalculator'
import type { HourSettings } from '../../settings/types'

function safeJsonParse(value: unknown): unknown {
  if (typeof value !== 'string' || value.trim() === '') return undefined
  try {
    return JSON.parse(value)
  } catch {
    return undefined
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Merge saved blueprint data with defaults, migrating old inputData→dataInputs */
function migrateBlueprintConfig(stored: Record<string, unknown>) {
  const merged = { ...getDefaultBlueprintConfig(), ...stored } as ReturnType<typeof getDefaultBlueprintConfig>
  // Migrate country rulesets: backfill missing fields from schema evolution
  if (Array.isArray(merged.countryRulesets)) {
    merged.countryRulesets = merged.countryRulesets.map((cr) => {
      const migrated = {
        ...cr,
        desiredPassRate: cr.desiredPassRate ?? '',
        passRateConfirmed: cr.passRateConfirmed ?? false,
        desiredPassRateConfirmed: cr.desiredPassRateConfirmed ?? false,
        nationalIds: Array.isArray(cr.nationalIds) ? cr.nationalIds : [],
        nationalIdNotes: cr.nationalIdNotes ?? '',
        docScanVendors: Array.isArray(cr.docScanVendors) ? cr.docScanVendors : ((cr as any).docScanVendor || (cr as any).biometricVendor ? [(cr as any).docScanVendor || (cr as any).biometricVendor] : []),
        selfieVendors: Array.isArray(cr.selfieVendors) ? cr.selfieVendors : ((cr as any).selfieVendor ? [(cr as any).selfieVendor] : []),
        deviceDataVendors: Array.isArray(cr.deviceDataVendors) ? cr.deviceDataVendors : ((cr as any).deviceDataVendor ? [(cr as any).deviceDataVendor] : []),
      }
      if (!Array.isArray(migrated.dataInputs) || migrated.dataInputs.length === 0) {
        return { ...migrated, dataInputs: getDefaultDataInputs() }
      }
      return {
        ...migrated,
        dataInputs: migrated.dataInputs.map((di) => ({ ...di, confirmed: (di as any).confirmed ?? false, captureMethod: (di as any).captureMethod ?? '', addressFormat: (di as any).addressFormat ?? '', notes: (di as any).notes ?? '' })),
      }
    })
  }
  return merged
}

function isRecipe(value: unknown): value is Recipe {
  if (!isRecord(value)) return false
  return typeof value.id === 'number' && typeof value.name === 'string' && isRecord(value.products)
}

function isWorkflow(value: unknown): value is Workflow {
  if (!isRecord(value)) return false

  const kind = value.kind === 'complex' ? 'complex' : 'builder'

  if (kind === 'complex') {
    return (
      typeof value.id === 'string' &&
      typeof value.name === 'string' &&
      typeof (value as any).workflowId === 'string' &&
      typeof (value as any).url === 'string'
    )
  }

  return typeof value.id === 'string' && typeof value.name === 'string' && Array.isArray((value as any).steps)
}

type PersistedAmlConfig = {
  amlCheckLevel?: AmlConfig['level']
  amlVendor?: string
  otherAmlVendor?: string
  amlRelationship?: AmlConfig['relationship']
  pepScreeningLevels?: string[]
  monitoringType?: AmlConfig['monitoringType']
  nameMatchingFuzziness?: number
  dataStorageProcessing?: {
    storeInAustralia?: boolean
    sendCountriesForPep?: boolean
  }
  customSanctionsLists?: string
  customWatchlists?: string
}

function isPersistedAmlConfig(value: unknown): value is PersistedAmlConfig {
  if (!isRecord(value)) return false
  // Only treat as old persisted format if it has old keys AND lacks new-format keys.
  // If both exist (hybrid from migration), treat as new format to preserve all fields.
  const hasOldKeys = 'amlCheckLevel' in value || 'amlVendor' in value || 'pepScreeningLevels' in value
  const hasNewKeys = 'level' in value || 'vendor' in value || 'sanctionsScreeningConfig' in value
  return hasOldKeys && !hasNewKeys
}

function mapPersistedAmlToUi(input: PersistedAmlConfig): Partial<AmlConfig> {
  const relationship =
    input.amlRelationship && (input.amlRelationship === 'unknown' || input.amlRelationship === 'frankieone' || input.amlRelationship === 'direct')
      ? input.amlRelationship
      : 'unknown'

  const vendor = input.amlVendor ?? ''

  const vendorWasFrankie = vendor === 'frankieone'

  return {
    level: input.amlCheckLevel ?? '',
    relationship: vendorWasFrankie ? 'frankieone' : relationship,
    vendor: vendorWasFrankie ? '' : vendor,
    otherVendor: input.otherAmlVendor ?? '',
    pepLevels: Array.isArray(input.pepScreeningLevels) ? input.pepScreeningLevels : [],
    monitoringType: input.monitoringType ?? '',
    fuzziness: typeof input.nameMatchingFuzziness === 'number' ? input.nameMatchingFuzziness : 0,
    storeInAustralia: Boolean(input.dataStorageProcessing?.storeInAustralia ?? false),
    sendCountriesForPep: Boolean(input.dataStorageProcessing?.sendCountriesForPep ?? false),
    customBlockList: [input.customSanctionsLists, input.customWatchlists].filter(Boolean).join('\n\n') || '',
  }
}

function deepMergeAmlConfig(defaults: AmlConfig, partial: Partial<AmlConfig>): AmlConfig {
  const merged = { ...defaults, ...partial } as any

  // Deep-merge nested objects: saved data may lack keys added after the deal was created.
  // Without deep merge, the ENTIRE default sub-object is replaced by the incomplete saved one.
  const nestedKeys: Array<keyof AmlConfig> = [
    'screeningEntityTypes',
    'pepScreeningConfig',
    'sanctionsScreeningConfig',
    'adverseMediaScreeningConfig',
    'additionalWatchlistsConfig',
    'batchProcessing',
    'jurisdictionScreening',
    'nameMatchingStrategy',
    'volumeEstimates',
  ]

  for (const key of nestedKeys) {
    if (defaults[key] && typeof defaults[key] === 'object' && !Array.isArray(defaults[key])) {
      merged[key] = { ...defaults[key], ...(partial[key] && typeof partial[key] === 'object' && !Array.isArray(partial[key]) ? partial[key] : {}) }
    }
  }

  return merged as AmlConfig
}

function normalizeAmlConfig(parsed: unknown): AmlConfig {
  const defaults = getDefaultAmlConfig()

  if (!isRecord(parsed)) return defaults

  if (isPersistedAmlConfig(parsed)) {
    return deepMergeAmlConfig(defaults, mapPersistedAmlToUi(parsed))
  }

  // Legacy UI schema (existing deals may have been saved as the UI/store shape)
  return deepMergeAmlConfig(defaults, parsed as Partial<AmlConfig>)
}

type PersistedRulesPayload = {
  rulesConfig?: unknown
  riskConfig?: unknown
} & Record<string, unknown>

function normalizeRiskConfig(parsed: unknown): RiskConfig {
  const defaults = getDefaultRiskConfig()
  if (!isRecord(parsed)) return defaults
  return { ...defaults, ...(parsed as Partial<RiskConfig>) }
}

export function mapDbScopeToStoreState(dbScope: Scope | null | undefined): ScopeData {
  const defaults = getDefaultScopeData()
  if (!dbScope) return defaults

  const generalParsed = safeJsonParse(dbScope.general_info)
  const kycParsed = safeJsonParse(dbScope.kyc_config)
  const amlParsed = safeJsonParse(dbScope.aml_config)
  const bioParsed = safeJsonParse(dbScope.biometrics_config)
  const kybParsed = safeJsonParse(dbScope.kyb_config)
  const recipesParsed = safeJsonParse(dbScope.recipes_config)
  const rulesParsed = safeJsonParse(dbScope.rules_config)
  const sowParsed = safeJsonParse((dbScope as any).sow_config)

  const rulesPayload = isRecord(rulesParsed) ? (rulesParsed as PersistedRulesPayload) : undefined
  const rulesConfigParsed = rulesPayload && 'rulesConfig' in rulesPayload ? rulesPayload.rulesConfig : rulesParsed
  const riskConfigParsed = rulesPayload && 'riskConfig' in rulesPayload ? rulesPayload.riskConfig : undefined

  const mergedGeneralInfo = { ...getDefaultGeneralInfo(), ...(isRecord(generalParsed) ? generalParsed : {}) } as any

  const createdAt = typeof dbScope.created_at === 'string' ? dbScope.created_at : null
  const updatedAt = typeof dbScope.updated_at === 'string' ? dbScope.updated_at : null

  if (!mergedGeneralInfo.scopeStatus) {
    mergedGeneralInfo.scopeStatus = 'draft'
  }

  if (!mergedGeneralInfo.scopeStatusDates || typeof mergedGeneralInfo.scopeStatusDates !== 'object') {
    mergedGeneralInfo.scopeStatusDates = { ...getDefaultGeneralInfo().scopeStatusDates }
  }

  if (!mergedGeneralInfo.scopeStatusDates.draft) {
    mergedGeneralInfo.scopeStatusDates.draft = createdAt || updatedAt || new Date().toISOString()
  }

  const mergedRulesConfig = { ...getDefaultRulesConfig(), ...(isRecord(rulesConfigParsed) ? rulesConfigParsed : {}) } as any

  if (mergedRulesConfig.riskRatingSystem === 'ours') {
    mergedRulesConfig.riskRatingSystem = 'frankieone'
  }

  return {
    ...defaults,
    customerName: typeof dbScope.customer_name === 'string' ? dbScope.customer_name : defaults.customerName,
    customerAbn: typeof (dbScope as any).customer_abn === 'string' ? (dbScope as any).customer_abn : defaults.customerAbn,
    hubspotDealId: typeof (dbScope as any).hubspot_deal_id === 'string' ? (dbScope as any).hubspot_deal_id : defaults.hubspotDealId,
    hubspotDealName: typeof (dbScope as any).hubspot_deal_name === 'string' ? (dbScope as any).hubspot_deal_name : defaults.hubspotDealName,
    generalInfo: mergedGeneralInfo,
    kycConfig: { ...getDefaultKycConfig(), ...(isRecord(kycParsed) ? kycParsed : {}) } as any,
    amlConfig: normalizeAmlConfig(amlParsed),
    biometricsConfig: { ...getDefaultBiometricsConfig(), ...(isRecord(bioParsed) ? bioParsed : {}) } as any,
    kybConfig: { ...getDefaultKybConfig(), ...(isRecord(kybParsed) ? kybParsed : {}) } as any,
    rulesConfig: mergedRulesConfig,
    riskConfig: normalizeRiskConfig(riskConfigParsed),
    sowConfig: { ...getDefaultSowConfig(mergedGeneralInfo.isUpsell ? 'upsell' : undefined), ...(isRecord(sowParsed) ? sowParsed : {}) } as any,
    sowCustomizedSections: (() => {
      const raw = (dbScope as any).sow_customized_sections
      if (typeof raw === 'string') {
        try { return JSON.parse(raw) } catch { return [] }
      }
      return Array.isArray(raw) ? raw : []
    })(),

    recipesConfig: Array.isArray(recipesParsed) ? recipesParsed.filter(isRecipe) : defaults.recipesConfig,
    workflowsConfig: Array.isArray(recipesParsed) ? recipesParsed.filter(isWorkflow) : defaults.workflowsConfig,
    scopeHours: defaults.scopeHours,
    featureRequests: defaults.featureRequests,

    workflowsNotes: typeof mergedGeneralInfo._workflowsNotes === 'string' ? mergedGeneralInfo._workflowsNotes : defaults.workflowsNotes,
    workflowsRequiredCount: typeof mergedGeneralInfo._workflowsRequiredCount === 'number' ? mergedGeneralInfo._workflowsRequiredCount : defaults.workflowsRequiredCount,

    notes: dbScope.notes ?? defaults.notes,

    blueprintConfig: migrateBlueprintConfig(isRecord(mergedGeneralInfo._blueprint) ? mergedGeneralInfo._blueprint : {}),

    finalized: mergedGeneralInfo._finalized === true,
    finalizedAt: typeof mergedGeneralInfo._finalizedAt === 'string' ? mergedGeneralInfo._finalizedAt : null,
    finalizedBy: typeof mergedGeneralInfo._finalizedBy === 'string' ? mergedGeneralInfo._finalizedBy : null,
  }
}

/**
 * Compute hour totals from scope data so they're persisted to the DB.
 * Uses the same logic as HoursTab and useScopeHours.
 *
 * Accepts HourSettings as a parameter (rather than importing the settings
 * store) so this module stays safe for worker-side imports.
 */
export function computeScopeHourTotals(scope: ScopeData, hourSettings: HourSettings) {
  const autoHours = calculateAutoHours(scope, hourSettings)
  const autoTotal = calculateTotalHours(autoHours)

  const manualTotal = scope.scopeHours.reduce((sum, h) => {
    const multiplier = COMPLEXITY_OPTIONS.find((c) => c.id === h.complexity)?.multiplier ?? 1
    return sum + h.estimatedHours * multiplier
  }, 0)

  const featureTotal = scope.featureRequests.reduce((sum, r) => sum + r.hours, 0)
  const totalHours = autoTotal + manualTotal + featureTotal
  const hourlyRate = 250
  const totalCost = totalHours * hourlyRate

  return {
    hourly_rate: hourlyRate,
    total_auto_hours: autoTotal,
    total_manual_hours: manualTotal,
    total_feature_hours: featureTotal,
    total_hours: totalHours,
    total_cost: totalCost,
  }
}

export function mapStoreStateToDbScope(scope: ScopeData, hourSettings?: HourSettings): Partial<Scope> {
  return {
    customer_name: scope.customerName || null,
    customer_abn: scope.customerAbn || null,
    general_info: JSON.stringify({
      ...scope.generalInfo,
      _blueprint: scope.blueprintConfig,
      _finalized: scope.finalized,
      _finalizedAt: scope.finalizedAt,
      _finalizedBy: scope.finalizedBy,
      _workflowsNotes: scope.workflowsNotes,
      _workflowsRequiredCount: scope.workflowsRequiredCount,
    }),
    kyc_config: JSON.stringify(scope.kycConfig),
    aml_config: JSON.stringify(scope.amlConfig),
    biometrics_config: JSON.stringify(scope.biometricsConfig),
    kyb_config: JSON.stringify(scope.kybConfig),
    recipes_config: JSON.stringify(scope.workflowsConfig),
    rules_config: JSON.stringify({ rulesConfig: scope.rulesConfig, riskConfig: scope.riskConfig }),
    sow_config: JSON.stringify(scope.sowConfig) as any,
    sow_customized_sections: JSON.stringify(scope.sowCustomizedSections || []) as any,

    notes: scope.notes || null,

    // Persist computed hour totals for server-side consumers
    // (e.g. the impl-review InjectImplFeeWidget reads scopes.total_cost).
    ...(hourSettings ? computeScopeHourTotals(scope, hourSettings) : {}),
  }
}

/**
 * Calculate the overall SOW "Known" percentage from a raw Scope API response.
 * This uses the same tab-completion logic as the ScopePage UI.
 */
export function calculateScopeKnownPercent(dbScope: Scope | null | undefined): number {
  return calculateScopeAndBlueprintPercent(dbScope).scopePercent
}

/**
 * Calculate both the overall scope % and the blueprint-specific %.
 */
export function calculateScopeAndBlueprintPercent(dbScope: Scope | null | undefined): { scopePercent: number; blueprintPercent: number } {
  const scopeData = mapDbScopeToStoreState(dbScope)
  const completions = calculateTabCompletion(scopeData)
  const EXCLUDED_FROM_OVERALL = new Set(['hours', 'actions', 'sow', 'custom_work', 'advanced'])
  const entries = Object.entries(completions).filter(([key]) => !EXCLUDED_FROM_OVERALL.has(key))
  const scopePercent = entries.length === 0 ? 0 : Math.round(entries.reduce((sum, [, val]) => sum + val, 0) / entries.length)
  const blueprintPercent = completions.blueprint ?? 0
  return { scopePercent, blueprintPercent }
}
